import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  Signal,
  TemplateRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TippyDirective } from '../../directives/tippy.directive';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { createSearchFilter } from '../../utils/search-filter.util';
import { ButtonComponent } from '../button/button.component';
import { CheckboxComponent } from '../form-controls/checkbox/checkbox.component';
import { InputComponent } from '../form-controls/input/input.component';
import { IconComponent } from '../icon/icon.component';
import { SpinnerComponent } from '../spinner/spinner.component';
import { EntityListSelectorService } from './entity-list-selector.service';
import { EntityConfig, EntityType } from './entity-list-selector.types';

/**
 * Generic reusable component for displaying and selecting entities (forms, assets, users, etc.)
 * Supports both inline display mode and dialog picker mode with single/multi selection.
 */
@Component({
  selector: 'app-entity-list-selector',
  imports: [
    IconComponent,
    SpinnerComponent,
    InputComponent,
    ButtonComponent,
    CheckboxComponent,
    TippyDirective,
    FormsModule,
    NgTemplateOutlet,
    TranslatePipe,
  ],
  templateUrl: './entity-list-selector.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './entity-list-selector.component.css',
})
export class EntityListSelectorComponent implements OnInit {
  private selectorService = inject(EntityListSelectorService);

  /** Which entity type we're selecting (forms, assets, users, etc.) */
  @Input() entityType!: EntityType;

  /** Selection mode: single item or multiple items */
  @Input() mode: 'single' | 'multi' = 'multi';

  /** IDs of items that should be pre-selected */
  @Input() preselected: string[] = [];

  /** Whether to show edit/delete action buttons (false for dialog picker mode) */
  @Input() showActions = true;

  /**
   * When true, shows a "Show inactive" checkbox that lets the user reveal
   * entities where `entityConfig.inactiveField` is falsy. Requires the entity
   * config to have `inactiveField` set; otherwise has no effect.
   */
  @Input() showInactiveFilter = false;

  /** Optional custom template for rendering each entity row */
  @Input() rowTemplate?: TemplateRef<any>;

  /** Emitted when selection changes (multi-select mode) */
  @Output() selectionChange = new EventEmitter<any[]>();

  /** Emitted when an item is selected (single-select mode) */
  @Output() select = new EventEmitter<any>();

  /** Emitted when edit button is clicked */
  @Output() edit = new EventEmitter<any>();

  /** Emitted when delete button is clicked */
  @Output() delete = new EventEmitter<any>();

  // Entity configuration loaded from service
  entityConfig!: EntityConfig;

  // Reactive query signals - assigned in ngOnInit from createQuery
  entities!: Signal<any[]>;
  loading!: Signal<boolean>;
  error!: Signal<string | null>;

  // Search functionality
  searchTerm = signal('');
  searchTermValue = ''; // For ngModel binding
  private searchTermSubject = new Subject<string>();

  // Selection tracking
  selectedIds = signal<Set<string>>(new Set());

  // Show-inactive toggle (only active when showInactiveFilter=true and entityConfig has inactiveField)
  showInactive = signal(false);

  // Computed filtered entities based on search term
  filteredEntities = computed(() => {
    let items = this.entities?.() ?? [];

    // Filter out inactive entities unless showInactive is true
    if (this.entityConfig?.inactiveField && !this.showInactive()) {
      items = items.filter((entity) => entity[this.entityConfig.inactiveField!]);
    }

    const term = this.searchTerm();
    if (!term || term.length < 2) {
      return items;
    }

    return createSearchFilter(items, term, this.entityConfig.searchFields, {
      caseSensitive: false,
      minLength: 2,
    });
  });

  // Computed selected entities
  selectedEntities = computed(() => {
    const ids = this.selectedIds();
    return this.entities().filter((entity) => ids.has(entity[this.entityConfig.idField]));
  });

  // Computed checkbox state for Select All - checks if ALL items are selected (not just filtered)
  selectAllChecked = computed(() => {
    const allEntities = this.entities();
    const current = this.selectedIds();
    return (
      allEntities.length > 0 &&
      allEntities.every((entity) => current.has(entity[this.entityConfig.idField]))
    );
  });

  constructor() {
    // Emit selection changes when selectedIds updates
    effect(() => {
      const selected = this.selectedEntities();
      this.selectionChange.emit(selected);
    });
  }

  async ngOnInit() {
    // Load entity configuration from service
    this.entityConfig = this.selectorService.getEntityConfig(this.entityType);

    // Create reactive query that auto-updates on sync changes
    const query = this.entityConfig.service.createQuery();
    this.entities = query.data;
    this.loading = query.loading;
    this.error = query.error;

    // Set up debounced search
    this.searchTermSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((term) => this.searchTerm.set(term));

    // Initialize preselected items
    if (this.preselected.length > 0) {
      this.selectedIds.set(new Set(this.preselected));
    }
  }

  /**
   * Handle entity selection (click on row)
   */
  selectEntity(entity: any): void {
    const id = entity[this.entityConfig.idField];

    if (this.mode === 'single') {
      // Single-select: emit immediately
      this.select.emit(entity);
      this.selectedIds.set(new Set([id]));
    } else {
      // Multi-select: toggle checkbox
      this.toggleSelection(id);
    }
  }

  /**
   * Toggle selection of an entity (multi-select mode)
   */
  toggleSelection(id: string): void {
    const current = this.selectedIds();
    const updated = new Set(current);

    if (updated.has(id)) {
      updated.delete(id);
    } else {
      updated.add(id);
    }

    this.selectedIds.set(updated);
  }

  /**
   * Check if an entity is selected
   */
  isSelected(entity: any): boolean {
    const id = entity[this.entityConfig.idField];
    return this.selectedIds().has(id);
  }

  /**
   * Handle search input changes with debouncing
   */
  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTermSubject.next(value);
  }

  /**
   * Clear search input and reset filter
   */
  clearSearch(): void {
    this.searchTermValue = '';
    this.searchTerm.set('');
    this.searchTermSubject.next('');
  }

  /**
   * Handle edit action
   */
  onEdit(entity: any, event: Event): void {
    event.stopPropagation();
    this.edit.emit(entity);
  }

  /**
   * Handle delete action
   */
  onDelete(entity: any, event: Event): void {
    event.stopPropagation();
    this.delete.emit(entity);
  }

  /**
   * Select/deselect ALL entities regardless of visibility (multi-select mode)
   */
  toggleSelectAll(): void {
    const allEntities = this.entities();
    const current = this.selectedIds();
    const allSelected = allEntities.every((entity: any) =>
      current.has(entity[this.entityConfig.idField]),
    );

    if (allSelected) {
      // Deselect all items
      this.selectedIds.set(new Set());
    } else {
      // Select all items
      const allIds = new Set(allEntities.map((entity: any) => entity[this.entityConfig.idField]));
      this.selectedIds.set(allIds);
    }
  }

  /**
   * Select all visible entities without deselecting hidden ones (multi-select mode)
   */
  selectVisible(): void {
    const filtered = this.filteredEntities();
    const updated = new Set(this.selectedIds());
    filtered.forEach((entity: any) => updated.add(entity[this.entityConfig.idField]));
    this.selectedIds.set(updated);
  }

  /**
   * Check if all visible entities are selected
   */
  areAllSelected(): boolean {
    const filtered = this.filteredEntities();
    const current = this.selectedIds();
    return (
      filtered.length > 0 &&
      filtered.every((entity: any) => current.has(entity[this.entityConfig.idField]))
    );
  }
}
